/**
 * Mock state manager — persists to sessionStorage so refreshes don't reset
 * progress mid-demo. State is per-tab so multiple reviewers can each play
 * through independently in their own browsers.
 *
 * Ported from uiv2 main branch as part of the mock-repo extraction.
 */

import { initialUsers, type MockUser } from "./data/users";
import { initialEnrollments, type MockEnrollment } from "./data/enrollments";
import { initialInviteCodes, type MockInviteCode } from "./data/inviteCodes";

const STORAGE_KEY = "rasse-mock-state";

export interface MockState {
  currentCallsign: string | null;
  currentRole: "admin" | "user" | null;
  isAuthenticated: boolean;
  jwt: string | null;

  users: MockUser[];
  enrollments: MockEnrollment[];
  inviteCodes: MockInviteCode[];

  inviteCounter: number;
}

function getDefaultState(): MockState {
  return {
    currentCallsign: null,
    currentRole: null,
    isAuthenticated: false,
    jwt: null,
    users: structuredClone(initialUsers),
    enrollments: structuredClone(initialEnrollments),
    inviteCodes: structuredClone(initialInviteCodes),
    inviteCounter: 100,
  };
}

let _state: MockState | null = null;

function load(): MockState {
  if (_state) return _state;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      _state = JSON.parse(raw) as MockState;
      return _state;
    }
  } catch {
    // corrupted — fall through to fresh state
  }
  _state = getDefaultState();
  save();
  return _state;
}

function save() {
  if (_state) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(_state));
  }
}

export function getState(): MockState {
  return load();
}

export function updateState(partial: Partial<MockState>) {
  const s = load();
  Object.assign(s, partial);
  save();
}

export function resetState() {
  _state = getDefaultState();
  save();
  // Clear the localStorage items the real app caches so the next reload
  // genuinely starts at the login screen.
  localStorage.removeItem("token");
  localStorage.removeItem("callsign");
  localStorage.removeItem("approveCode");
}

export function generateMockJwt(callsign: string): string {
  // A non-cryptographic, browser-shaped JWT. Good enough to satisfy code
  // paths that decode/forward the token; not good enough to fool any verifier.
  const header = btoa(JSON.stringify({ alg: "HS256", typ: "JWT" }));
  const payload = btoa(
    JSON.stringify({
      sub: callsign,
      iss: "mock-rasenmaeher",
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 86400,
      CN: callsign,
    }),
  );
  const sig = btoa("mock-signature");
  return `${header}.${payload}.${sig}`;
}

const PHONETIC_WORDS = [
  "ALPHA",
  "BRAVO",
  "CHARLIE",
  "DELTA",
  "ECHO",
  "FOXTROT",
  "GOLF",
  "HOTEL",
  "INDIA",
  "JULIET",
  "KILO",
  "LIMA",
];

export function generateInviteCode(): string {
  const s = load();
  s.inviteCounter++;
  save();
  const w1 = PHONETIC_WORDS[Math.floor(Math.random() * PHONETIC_WORDS.length)];
  const w2 = PHONETIC_WORDS[Math.floor(Math.random() * PHONETIC_WORDS.length)];
  return `${w1}-${w2}-${s.inviteCounter}`;
}

export function generateApproveCode(callsign: string): string {
  const prefix = callsign.substring(0, 4).toUpperCase();
  const num = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${num}`;
}
